/**
 * Цена перекрытия: пара «противоположная исходная + кандидат», не три исхода.
 *
 * `overlayHand` с `side: X` дописывает третью взаимоисключающую ногу — любой
 * живой кэф тогда даёт огромный минус. Перекрытие — замена закрытой линии, и
 * процент обязан быть тем же, что у обычной двухногой вилки (`calc.ts`).
 */

import { Decimal } from 'decimal.js';

import { apiUrl } from './build';
import { type Odds, bookmakerTicket, payout, roiGross, stakes } from './calc';
import type { RowLeg } from './row';

export interface CoverQuote {
  s: string;
  side?: string;
  odds: string;
  age?: number;
  url?: string | null;
  betUrl?: string | null;
  p?: string;
  name?: string | null;
  participants?: string[];
  unit?: 'share' | 'money';
  cur?: string;
  step?: string;
}

export interface CoverPrice {
  /** Доля, не проценты: 0.014 = 1.4 %. */
  roi: Decimal;
  /** Рубли после округления ставок. Знак: плюс, минус или ноль. */
  profit: Decimal;
  stakes: [Decimal, Decimal];
}

export interface PricedCover {
  quote: CoverQuote;
  price: CoverPrice;
}

/** Пара противоположной исходной ноги и кандидата. Третий кэф сюда не входит. */
export function coverPrice(oppositeOdds: Odds, candidateOdds: Odds, bank: Odds): CoverPrice {
  const pair: Odds[] = [oppositeOdds, candidateOdds];
  const raw = stakes(bank, pair);
  const first = bookmakerTicket(raw[0]!, pair[0]!);
  const second = bookmakerTicket(raw[1]!, pair[1]!);
  const total = first.stake.plus(second.stake);
  const worst = first.payout.lt(second.payout) ? first.payout : second.payout;
  const profit = worst.minus(total);
  return {
    roi: total.gt(0) ? profit.div(total) : new Decimal(0),
    profit,
    stakes: [first.stake, second.stake],
  };
}

/**
 * Контроль: тройка исходов — это испорченная вилка, не перекрытие.
 * Для сверки с тем, чего на экране быть не должно.
 */
export function threeWayGross(a: Odds, b: Odds, c: Odds): Decimal {
  return roiGross([a, b, c]);
}

export function coverPctText(roi: Decimal): string {
  const n = roi.times(100);
  if (n.abs().lt('0.005')) return '0.00 %';
  const body = n.abs().toFixed(2);
  return n.gt(0) ? `+${body} %` : `−${body} %`;
}

export function bookAllowed(
  source: string,
  venues: readonly string[],
  venueMode: 'only' | 'except' | 'main',
): boolean {
  if (venues.length === 0) return true;
  const hit = venues.includes(source);
  return venueMode === 'except' ? !hit : hit;
}

export function pricedCovers(
  quotes: readonly CoverQuote[],
  oppositeOdds: Odds,
  bank: Odds,
  venues: readonly string[] = [],
  venueMode: 'only' | 'except' | 'main' = 'except',
): PricedCover[] {
  const out: PricedCover[] = [];
  for (const quote of quotes) {
    if (!bookAllowed(quote.s, venues, venueMode)) continue;
    if (!quote.odds) continue;
    try {
      out.push({ quote, price: coverPrice(oppositeOdds, quote.odds, bank) });
    } catch {
      // Битая строка кэфа не роняет список: её просто нет.
    }
  }
  out.sort((left, right) => {
    const byMoney = right.price.profit.cmp(left.price.profit);
    if (byMoney !== 0) return byMoney;
    return new Decimal(right.quote.odds).cmp(new Decimal(left.quote.odds));
  });
  return out;
}

export function coverLeg(quote: CoverQuote, stakeRub: number): RowLeg {
  const name = quote.name ?? '';
  return {
    source: quote.s,
    name,
    who: quote.participants ?? [],
    side: quote.side ?? '',
    unit: quote.unit === 'share' ? 'share' : 'money',
    cur: quote.cur ?? 'RUB',
    headline: String(Math.round(stakeRub)),
    detail: quote.odds,
    rub: stakeRub,
    payout: payout(String(stakeRub), quote.odds).toNumber(),
    usd: null,
    shares: null,
    cents: null,
    odds: quote.odds,
    lay: false,
    layStake: null,
    cap: { kind: 'assumed', value: null },
    asset: null,
    tick: null,
    minSize: null,
    precision: quote.p ?? 'section',
    url: quote.betUrl ?? quote.url ?? null,
  };
}

export type CoverFail = 'denied' | 'gone' | 'fail';

export type CoverFetch =
  | { ok: true; quotes: CoverQuote[] }
  | { ok: false; reason: CoverFail };

export async function fetchCovers(forkId: string, side: string): Promise<CoverFetch> {
  try {
    const res = await fetch(
      apiUrl(`/v1/fork/${encodeURIComponent(forkId)}/covers?side=${encodeURIComponent(side)}`),
      { credentials: 'same-origin' },
    );
    if (res.status === 402) return { ok: false, reason: 'denied' };
    if (res.status === 404) return { ok: false, reason: 'gone' };
    if (!res.ok) return { ok: false, reason: 'fail' };
    const body = (await res.json()) as { legs?: CoverQuote[] };
    return { ok: true, quotes: Array.isArray(body.legs) ? body.legs : [] };
  } catch {
    return { ok: false, reason: 'fail' };
  }
}
